import { useState, useRef } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { Upload } from 'lucide-react'
import Cropper from 'react-easy-crop'
import type { Point, Area } from 'react-easy-crop'

interface ImageUploadModalProps {
	/** Label for the upload button */
	label?: string
	/** Trigger button text */
	buttonText?: string
	/** Trigger button variant */
	buttonVariant?: 'default' | 'outline' | 'ghost'
	/** Crop aspect ratio (1 for square, 16/9 for landscape, etc.) */
	aspectRatio?: number
	/** Maximum file size in bytes */
	maxFileSize?: number
	/** Accepted file types */
	acceptedFileTypes?: string[]
	/** Current image URL for editing */
	initialImageUrl?: string
	/** Callback when image is successfully cropped and processed */
	onImageCropped?: (
		imageBlob: Blob,
		croppedImageUrl: string,
	) => Promise<void> | void
	/** Callback when upload fails */
	onError?: (error: Error) => void
	/** Whether the component is disabled */
	disabled?: boolean
	/** Whether the field is required */
	required?: boolean
	/** Custom CSS classes */
	className?: string
}

// Function to create an image from a url
const createImage = (url: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image()
		image.crossOrigin = 'anonymous'
		image.addEventListener('load', () => resolve(image))
		image.addEventListener('error', (error) => reject(error))
		image.src = url
	})

// Function to get the cropped image
async function getCroppedImg(
	imageSrc: string,
	pixelCrop: Area,
	rotation = 0,
): Promise<{ blob: Blob; url: string }> {
	try {
		const image = await createImage(imageSrc)
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')

		if (!ctx) {
			throw new Error('No 2d context')
		}

		// Set canvas size to match the desired crop size
		canvas.width = pixelCrop.width
		canvas.height = pixelCrop.height

		// Draw rotated image
		ctx.translate(canvas.width / 2, canvas.height / 2)
		ctx.rotate((rotation * Math.PI) / 180)
		ctx.translate(-canvas.width / 2, -canvas.height / 2)

		// Draw the image with crop
		ctx.drawImage(
			image,
			pixelCrop.x,
			pixelCrop.y,
			pixelCrop.width,
			pixelCrop.height,
			0,
			0,
			pixelCrop.width,
			pixelCrop.height,
		)

		return new Promise((resolve, reject) => {
			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error('Canvas is empty'))
						return
					}
					resolve({
						blob,
						url: canvas.toDataURL('image/webp', 0.75),
					})
				},
				'image/webp',
				0.75,
			)
		})
	} catch (error) {
		console.error('Error in getCroppedImg:', error)
		throw error
	}
}

export default function ImageUploadModal({
	label,
	buttonText = 'อัปโหลดรูปภาพ',
	buttonVariant = 'outline',
	aspectRatio = 1,
	maxFileSize = 5 * 1024 * 1024, // 5MB
	acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp'],
	initialImageUrl,
	onImageCropped,
	onError,
	disabled = false,
	required = false,
	className,
}: ImageUploadModalProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [imageSrc, setImageSrc] = useState<string>('')
	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1)
	const [rotation, setRotation] = useState(0)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
	const [isProcessing, setIsProcessing] = useState(false)

	const handleGifUpload = async (file: File) => {
		const img = new Image()
		const objectUrl = URL.createObjectURL(file)

		img.onload = async () => {
			URL.revokeObjectURL(objectUrl)

			const imageRatio = img.width / img.height
			const tolerance = 0.01

			// Check if aspect ratio matches
			if (Math.abs(imageRatio - aspectRatio) > tolerance) {
				const ratioText =
					aspectRatio === 1 ? '1:1' : `${Math.round(aspectRatio * 100)}:100`
				const errorMessage = `GIF ต้องมีอัตราส่วน ${ratioText} (เช่น ${Math.round(1000 * aspectRatio)}x1000 หรือ ${Math.round(500 * aspectRatio)}x500)\nไฟล์ที่อัพโหลด: ${img.width}x${img.height}`
				showErrorToast(errorMessage)
				onError?.(new Error(errorMessage))
				return
			}

			// Dimensions match, process GIF directly without cropping
			setIsProcessing(true)
			try {
				// Convert file to blob
				const blob = new Blob([file], { type: file.type })
				const url = URL.createObjectURL(blob)

				// Call the callback with the GIF blob
				await onImageCropped?.(blob, url)

				toast.success('อัปโหลด GIF สำเร็จ')
			} catch (error) {
				console.error('Error processing GIF:', error)
				showErrorToast('ไม่สามารถประมวลผล GIF ได้ กรุณาลองใหม่อีกครั้ง')
				onError?.(error as Error)
			} finally {
				setIsProcessing(false)
			}
		}

		img.onerror = () => {
			URL.revokeObjectURL(objectUrl)
			showErrorToast('ไม่สามารถอ่านไฟล์ GIF ได้')
		}

		img.src = objectUrl
	}

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			// Validate file type
			if (!acceptedFileTypes.includes(file.type)) {
				const acceptedTypesText = acceptedFileTypes
					.map((type) => type.split('/')[1].toUpperCase())
					.join(', ')
				const errorMessage = `ไฟล์ประเภทนี้ไม่รองรับ กรุณาเลือกไฟล์ ${acceptedTypesText}`
				showErrorToast(errorMessage)
				onError?.(new Error(errorMessage))
				return
			}

			// Validate file size
			if (file.size > maxFileSize) {
				const maxSizeMB = Math.round(maxFileSize / (1024 * 1024))
				const errorMessage = `ขนาดไฟล์ใหญ่เกินไป (สูงสุด ${maxSizeMB}MB)`
				showErrorToast(errorMessage)
				onError?.(new Error(errorMessage))
				return
			}

			// Check if file is GIF
			if (file.type === 'image/gif') {
				handleGifUpload(file)
				return
			}

			const reader = new FileReader()
			reader.addEventListener('load', () => {
				setImageSrc(reader.result as string)
				setIsOpen(true)
				// Reset crop state
				setCrop({ x: 0, y: 0 })
				setZoom(1)
				setRotation(0)
				setCroppedAreaPixels(null)
			})
			reader.readAsDataURL(file)
		}
	}

	const handleUploadClick = (event: React.MouseEvent) => {
		event.preventDefault()
		fileInputRef.current?.click()
	}

	const handleCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
		setCroppedAreaPixels(croppedAreaPixels)
	}

	const handleConfirm = async () => {
		if (!imageSrc || !croppedAreaPixels) {
			showErrorToast('ไม่พบข้อมูลรูปภาพที่จะครอบ')
			return
		}

		setIsProcessing(true)
		try {
			// Get the cropped image
			const { blob, url: croppedImageUrl } = await getCroppedImg(
				imageSrc,
				croppedAreaPixels,
				rotation,
			)

			// Call the callback with the cropped image
			await onImageCropped?.(blob, croppedImageUrl)

			// Close modal and reset state
			setIsOpen(false)
			setImageSrc('')
			setCrop({ x: 0, y: 0 })
			setZoom(1)
			setRotation(0)
			setCroppedAreaPixels(null)
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}

			toast.success('รูปภาพถูกครอบและประมวลผลเรียบร้อยแล้ว')
		} catch (error) {
			console.error('Error cropping image:', error)
			const errorMessage = 'ไม่สามารถประมวลผลรูปภาพได้ กรุณาลองใหม่อีกครั้ง'
			showErrorToast(errorMessage)
			onError?.(error as Error)
		} finally {
			setIsProcessing(false)
		}
	}

	const handleCancel = () => {
		setIsOpen(false)
		setImageSrc('')
		setCrop({ x: 0, y: 0 })
		setZoom(1)
		setRotation(0)
		setCroppedAreaPixels(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	return (
		<div className={className}>
			{label && (
				<label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
					{label}
					{required && <span className="ml-1 text-red-500">*</span>}
				</label>
			)}

			{/* Current Image Display */}
			{initialImageUrl && (
				<div className="relative mb-4 h-32 w-32 overflow-hidden rounded-lg border">
					<img
						src={initialImageUrl}
						alt="Current image"
						className="h-full w-full object-cover"
					/>
				</div>
			)}

			{/* Upload Button */}
			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogTrigger asChild>
					<Button
						type="button"
						variant={buttonVariant}
						size="sm"
						className="w-full"
						onClick={handleUploadClick}
						disabled={disabled}
					>
						<Upload className="mr-2 h-4 w-4" />
						{initialImageUrl ? 'เปลี่ยนรูปภาพ' : buttonText}
					</Button>
				</DialogTrigger>
				<DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>ครอบรูปภาพ</DialogTitle>
					</DialogHeader>
					{imageSrc && (
						<div className="space-y-4">
							{/* Crop Area */}
							<div className="relative h-96 overflow-hidden rounded-lg bg-gray-100">
								<Cropper
									image={imageSrc}
									crop={crop}
									rotation={rotation}
									zoom={zoom}
									aspect={aspectRatio}
									onCropChange={setCrop}
									onRotationChange={setRotation}
									onZoomChange={setZoom}
									onCropComplete={handleCropComplete}
									showGrid={false}
									cropShape="rect"
									objectFit="contain"
									style={{
										containerStyle: {
											width: '100%',
											height: '100%',
											backgroundColor: '#f3f4f6',
										},
									}}
								/>
							</div>

							{/* Controls */}
							<div className="grid grid-cols-2 gap-4">
								{/* Zoom Control */}
								<div className="space-y-2">
									<label className="text-sm font-medium">ขยาย/ย่อ</label>
									<input
										type="range"
										value={zoom}
										min={1}
										max={3}
										step={0.1}
										onChange={(e) => setZoom(Number(e.target.value))}
										className="w-full"
									/>
									<div className="text-xs text-muted-foreground">
										{(zoom * 100).toFixed(0)}%
									</div>
								</div>

								{/* Rotation Control */}
								<div className="space-y-2">
									<label className="text-sm font-medium">หมุน</label>
									<input
										type="range"
										value={rotation}
										min={-180}
										max={180}
										step={1}
										onChange={(e) => setRotation(Number(e.target.value))}
										className="w-full"
									/>
									<div className="text-xs text-muted-foreground">
										{rotation}°
									</div>
								</div>
							</div>

							{/* Action Buttons */}
							<div className="flex justify-end space-x-2 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={handleCancel}
									disabled={isProcessing}
								>
									ยกเลิก
								</Button>
								<Button
									type="button"
									onClick={handleConfirm}
									disabled={isProcessing}
								>
									{isProcessing ? 'กำลังประมวลผล...' : 'ยืนยันการครอบ'}
								</Button>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Hidden File Input */}
			<input
				ref={fileInputRef}
				type="file"
				accept={acceptedFileTypes.join(',')}
				onChange={handleFileSelect}
				className="hidden"
				disabled={disabled}
			/>

			{/* Help Text */}
			<p className="mt-2 text-sm text-muted-foreground">
				รองรับไฟล์{' '}
				{acceptedFileTypes
					.map((type) => type.split('/')[1].toUpperCase())
					.join(', ')}{' '}
				(สูงสุด {Math.round(maxFileSize / (1024 * 1024))}MB)
				{aspectRatio === 1 ? (
					<>
						{' '}
						• รูปภาพจะถูกครอบเป็นสี่เหลี่ยมจัตุรัส{' '}
						<span className="text-amber-600 dark:text-amber-400">(1:1)</span>
					</>
				) : (
					<>
						{' '}
						• อัตราส่วน{' '}
						<span className="text-amber-600 dark:text-amber-400">
							{aspectRatio === Math.floor(aspectRatio)
								? `${aspectRatio}:1`
								: `${Math.round(aspectRatio * 100)}:100`}
						</span>
					</>
				)}
				{acceptedFileTypes.includes('image/gif') &&
					' • GIF ต้องมีอัตราส่วนที่ถูกต้อง (ไม่สามารถครอบได้)'}
			</p>
		</div>
	)
}
